import React from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, MessageSquare, RefreshCw, Plus, Eye, Edit3, ArrowRight, Zap, BookOpen, Check, Meh, Book } from 'react-feather';

export const TeachingHelpContent: React.FC = () => {
  return (
    <div className="space-y-8 text-sm">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-eddy-blue/10 to-eddy-green/10 border border-eddy-blue/30 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-eddy-primary mb-3 flex items-center gap-3">
          <BookOpen size={24} className="text-eddy-blue" />
          Interactive Chat Teaching System
        </h2>
        <p className="text-gray-700 text-base leading-relaxed mb-4">
          <strong>A primary feature</strong> of Eddy is the ability to teach the system new knowledge through multiple pathways. 
          You can type <code className="bg-eddy-blue/20 px-2 py-1 rounded text-eddy-blue font-mono">teach</code> in chat, 
          use the "Teach Eddy" button after unsatisfactory responses, or create teachings directly through the admin interface. 
          All methods help improve Eddy's knowledge base with AI-powered content enhancement.
        </p>
        <div className="flex items-center gap-2 text-eddy-green font-medium">
          <Zap size={16} />
          <span>Real-time knowledge enhancement with immediate integration</span>
        </div>
      </section>

      {/* How to Use */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4 flex items-center gap-2">
          <MessageSquare size={20} />
          Three Ways to Teach Eddy New Knowledge
        </h3>
        

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-eddy-blue/30 rounded-lg p-4">
            <h4 className="font-semibold text-eddy-blue mb-2 flex items-center gap-2">
              <span className="bg-eddy-blue text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
              Chat "teach" Command
            </h4>
            <div className="font-mono bg-gray-50 p-3 rounded border text-sm mb-2">
              <div className="text-gray-600 mb-1">You type:</div>
              <div className="text-eddy-blue">teach</div>
            </div>
            <p className="text-xs text-gray-600">Opens teaching form with conversation context pre-loaded</p>
          </div>
          
          <div className="bg-white border border-eddy-green/30 rounded-lg p-4">
            <h4 className="font-semibold text-eddy-green mb-2 flex items-center gap-2">
              <span className="bg-eddy-green text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
              "Teach Eddy" Button
            </h4>
            <div className="text-sm space-y-1 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">❓ "Did this answer your question?"</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">→ If you're an authenticated admin</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">→ "Teach Eddy" button appears with satisfaction options</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">→ Click button to improve Eddy's knowledge</span>
              </div>
            </div>
            <p className="text-xs text-gray-600">Teaching form opens with question/answer context pre-loaded</p>
          </div>

          <div className="bg-white border border-eddy-orange/30 rounded-lg p-4">
            <h4 className="font-semibold text-eddy-orange mb-2 flex items-center gap-2">
              <span className="bg-eddy-orange text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">3</span>
              Admin "Create Teaching"
            </h4>
            <div className="text-sm space-y-1 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">📋 Go to Admin → Teaching</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">→ Click "Create Teaching"</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">→ Fill form manually</span>
              </div>
            </div>
            <p className="text-xs text-gray-600">Same form but without conversation context</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Zap size={16} />
            Universal Teaching Form Workflow
          </h4>
          <p className="text-sm text-amber-700 mb-3">
            All three methods above open the same powerful teaching form with these steps:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border border-amber-200">
              <div className="font-semibold text-amber-800 mb-1">📝 Fill Form</div>
              <div className="text-amber-700">Enter teaching content, select priority and source</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-amber-200">
              <div className="font-semibold text-amber-800 mb-1">🤖 AI Enhancement</div>
              <div className="text-amber-700">GPT-4 generates comprehensive, formatted content</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-amber-200">
              <div className="font-semibold text-amber-800 mb-1">✅ Approve/Refine</div>
              <div className="text-amber-700">Review, request changes, or approve final content</div>
            </div>
          </div>
        </div>
      </section>

      {/* Satisfaction-Based Teaching */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4 flex items-center gap-2">
          <Check size={20} />
          Satisfaction-Based Teaching Workflow
        </h3>
        
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-base leading-relaxed">
            After every Eddy response, you'll see <strong>"Did this answer your question?"</strong> - this is your opportunity 
            to improve Eddy's knowledge when answers are incomplete or incorrect. Perfect for real-time corrections!
          </p>
        </div>

        <div className="space-y-4">
          {/* Step 1: Satisfaction Check */}
          <div className="border border-amber-300 rounded-lg overflow-hidden">
            <div className="bg-amber-100 px-4 py-3 border-b border-amber-300">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertCircle size={16} />
                Step 1: Answer Satisfaction (For Authenticated Admin Users)
              </h4>
            </div>
            <div className="p-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={16} className="text-blue-600" />
                  <span className="font-semibold text-blue-800">Authentication Required</span>
                </div>
                <p className="text-sm text-blue-700">
                  The "Teach Eddy" button only appears if you are <strong>logged in as an authenticated admin user</strong>. 
                  It appears alongside all satisfaction options (Yes/Partially/No) regardless of which one you might choose.
                </p>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <Check size={20} className="text-green-600 mx-auto mb-2" />
                  <div className="font-semibold text-green-800 text-sm">Yes</div>
                  <div className="text-xs text-green-600">Perfect answer!</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <Meh size={20} className="text-yellow-600 mx-auto mb-2" />
                  <div className="font-semibold text-yellow-800 text-sm">Partially</div>
                  <div className="text-xs text-yellow-600">Missing info</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <XCircle size={20} className="text-red-600 mx-auto mb-2" />
                  <div className="font-semibold text-red-800 text-sm">No</div>
                  <div className="text-xs text-red-600">Incorrect answer</div>
                </div>
                <div className="bg-eddy-green/10 border border-eddy-green/30 rounded-lg p-3 text-center">
                  <Book size={20} className="text-eddy-green mx-auto mb-2" />
                  <div className="font-semibold text-eddy-green text-sm">Teach Eddy</div>
                  <div className="text-xs text-eddy-green">Admin only</div>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <strong>What happens:</strong> Authenticated admin users see the "Teach Eddy" button alongside satisfaction options, 
                allowing them to improve Eddy's knowledge regardless of their satisfaction rating.
              </div>
            </div>
          </div>

          {/* Step 2: Teaching Option */}
          <div className="border border-eddy-green/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-green/10 px-4 py-3 border-b border-eddy-green/30">
              <h4 className="font-semibold text-eddy-green flex items-center gap-2">
                <Book size={16} />
                Step 2: "Teach Eddy" Button
              </h4>
            </div>
            <div className="p-4">
              <div className="bg-white border rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">For authenticated admin users, you'll see:</span>
                </div>
                <button className="bg-eddy-green text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm">
                  <Book size={16} />
                  Teach Eddy
                </button>
              </div>
              <div className="text-sm text-gray-600">
                <strong>What happens:</strong> Clicking opens the comprehensive teaching form with conversation context pre-loaded, 
                allowing you to improve Eddy's knowledge regardless of your satisfaction level.
              </div>
            </div>
          </div>

          {/* Step 3: Form Workflow */}
          <div className="border border-eddy-blue/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-blue/10 px-4 py-3 border-b border-eddy-blue/30">
              <h4 className="font-semibold text-eddy-blue flex items-center gap-2">
                <ArrowRight size={16} />
                Step 3: Teaching Form & Preview Workflow
              </h4>
            </div>
            <div className="p-4 text-sm text-gray-600">
              <p className="mb-3">
                <strong>Same powerful workflow as direct teaching:</strong> The teaching form opens with conversation context 
                pre-loaded, you fill in the correct information, GPT-4 generates enhanced content, and you approve or refine it.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <strong>Key advantage:</strong> Context-aware teaching! The system knows exactly what question was asked 
                and what answer was given, making the generated teaching content much more targeted and relevant.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teaching Form Details */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4">Teaching Form Workflow Details</h3>
        
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-base leading-relaxed">
            Whether you use <strong>chat "teach" command</strong>, <strong>"Teach Eddy" button</strong>, or <strong>admin "Create Teaching"</strong> - 
            they all open the same comprehensive teaching form with AI-powered enhancement and preview workflow.
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Form Input */}
          <div className="border border-eddy-blue/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-blue/10 px-4 py-3 border-b border-eddy-blue/30">
              <h4 className="font-semibold text-eddy-blue flex items-center gap-2">
                <Edit3 size={16} />
                Step 1: Fill Teaching Form
              </h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white border rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">Form Fields</h5>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• <strong>Query/Question:</strong> What question this teaching answers</li>
                    <li>• <strong>Teaching Content:</strong> Your knowledge/correction</li>
                    <li>• <strong>Content Source:</strong> Which knowledge base</li>
                    <li>• <strong>Priority:</strong> How important this is (Critical/High/Medium/Low)</li>
                    <li>• <strong>Type:</strong> Correction/New Content/Clarification/Update</li>
                  </ul>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">Pre-loaded Context</h5>
                  <div className="text-sm text-gray-700">
                    <p className="mb-2">From chat methods, you get:</p>
                    <ul className="space-y-1">
                      <li>• Original question automatically filled</li>
                      <li>• Conversation history included</li>
                      <li>• Context for better AI enhancement</li>
                    </ul>
                    <p className="mt-2 text-gray-600">Admin method: Manual entry only</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <strong>What happens:</strong> Once you fill the form and click "Generate Preview", GPT-4 processes your input 
                  to create comprehensive, well-formatted teaching content with proper structure and detail.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2: AI Preview */}
          <div className="border border-eddy-green/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-green/10 px-4 py-3 border-b border-eddy-green/30">
              <h4 className="font-semibold text-eddy-green flex items-center gap-2">
                <BookOpen size={16} />
                Step 2: AI-Enhanced Preview
              </h4>
            </div>
            <div className="p-4">
              <div className="bg-white border rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Your Original Input</h5>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      "Orders can be cancelled within 24 hours by contacting support"
                    </div>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">AI-Enhanced Version</h5>
                    <div className="bg-eddy-green/5 p-3 rounded text-sm">
                      <strong>## Order Cancellation Policy</strong><br/>
                      Customers can cancel orders within <strong>24 hours</strong>...<br/>
                      <strong>### How to Cancel</strong><br/>
                      1. Call customer support...
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <strong>What happens:</strong> GPT-4 creates professional documentation with markdown formatting, 
                  comprehensive coverage, examples, and structured organization based on your input and conversation context.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3: Approval Options */}
          <div className="border border-eddy-orange/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-orange/10 px-4 py-3 border-b border-eddy-orange/30">
              <h4 className="font-semibold text-eddy-orange flex items-center gap-2">
                <ArrowRight size={16} />
                Step 3: Review & Approval
              </h4>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* Approve Option */}
                <div className="bg-eddy-green/5 border border-eddy-green/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle size={18} className="text-eddy-green" />
                    <span className="font-semibold text-eddy-green">Approve & Create</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <p className="mb-2">Content looks good!</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Immediately saved and indexed</li>
                      <li>• High priority scoring (800-1000+ points)</li>
                      <li>• Available in responses right away</li>
                      <li>• Status: "pending" for admin review</li>
                    </ul>
                  </div>
                </div>

                {/* Request Changes */}
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle size={18} className="text-amber-600" />
                    <span className="font-semibold text-amber-600">Request Changes</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <p className="mb-2">Needs refinement</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Describe desired changes</li>
                      <li>• AI regenerates with updates</li>
                      <li>• Back to preview step</li>
                      <li>• Can iterate multiple times</li>
                    </ul>
                  </div>
                </div>

                {/* Cancel */}
                <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle size={18} className="text-red-600" />
                    <span className="font-semibold text-red-600">Cancel</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <p className="mb-2">Don't save anything</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Closes the form</li>
                      <li>• No content saved</li>
                      <li>• Returns to chat/admin</li>
                      <li>• No impact on knowledge base</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admin Create Teaching */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4 flex items-center gap-2">
          <Plus size={20} />
          Admin Create Teaching Function
        </h3>
        
        <div className="bg-gradient-to-r from-eddy-green/10 to-eddy-blue/10 border border-eddy-green/30 rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-base">
            The <strong>"Create Teaching"</strong> button in this admin interface provides a different workflow from the chat-based teaching methods. 
            Unlike the AI-enhanced preview workflow used in chat, the admin method uses a <strong>direct form submission</strong> - 
            you manually write all content and it's saved immediately without AI enhancement or preview steps.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <AlertCircle size={16} />
            Key Difference: No AI Preview Workflow
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-amber-700">
            <div>
              <h5 className="font-semibold mb-2">💬 Chat Teaching Methods</h5>
              <ul className="space-y-1">
                <li>• Fill form → Generate AI preview → Review → Approve/Refine → Create</li>
                <li>• AI enhances your content with GPT-4</li>
                <li>• Interactive preview and approval process</li>
                <li>• Can iterate and refine content</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-2">⚙️ Admin Create Teaching</h5>
              <ul className="space-y-1">
                <li>• Fill form → Click "Create" → Teaching saved immediately</li>
                <li>• You write all content manually</li>
                <li>• No AI enhancement or preview</li>
                <li>• Direct submission to database</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* When to Use */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-eddy-blue" />
              When to Use Admin Create Teaching
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h5 className="font-medium text-eddy-green">✅ Best for:</h5>
                <ul className="space-y-1 text-sm text-gray-700 ml-4">
                  <li>• Bulk content creation sessions</li>
                  <li>• Planned knowledge base updates</li>
                  <li>• Policy documentation uploads</li>
                  <li>• Structured content organization</li>
                  <li>• When you have pre-written content</li>
                  <li>• Training content from documents</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium text-eddy-orange">💬 Chat Teaching Better for:</h5>
                <ul className="space-y-1 text-sm text-gray-700 ml-4">
                  <li>• Real-time corrections during conversations</li>
                  <li>• Context-aware teaching from Q&A</li>
                  <li>• Immediate response improvements</li>
                  <li>• Conversational knowledge capture</li>
                  <li>• User-driven corrections</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step-by-Step Process */}
          <div className="border border-eddy-blue/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-blue/10 px-4 py-3 border-b border-eddy-blue/30">
              <h4 className="font-semibold text-eddy-blue">Step-by-Step: Using Create Teaching</h4>
            </div>
            <div className="p-4 space-y-4">
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-eddy-blue text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Click "Create Teaching" Button</h5>
                  <p className="text-gray-700 text-sm">Located in the top action bar next to "Refresh Vector Store". Opens the teaching creation modal.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-eddy-orange text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Fill Out the Teaching Form</h5>
                  <div className="bg-white border rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-medium text-gray-900">📝 Query/Question</div>
                        <div className="text-gray-600">The question this teaching answers</div>
                        <div className="bg-gray-50 p-2 rounded text-xs font-mono mt-1">
                          "How do I cancel my subscription?"
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">🏷️ Content Source</div>
                        <div className="text-gray-600">Which knowledge base this belongs to</div>
                        <div className="bg-gray-50 p-2 rounded text-xs mt-1">
                          Support Center, Admin Help, etc.
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">⭐ Priority</div>
                        <div className="text-gray-600">How important this teaching is</div>
                        <div className="bg-gray-50 p-2 rounded text-xs mt-1">
                          High, Medium, Low, Critical
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">📂 Type</div>
                        <div className="text-gray-600">Category of teaching content</div>
                        <div className="bg-gray-50 p-2 rounded text-xs mt-1">
                          FAQ, Process, Feature, Correction
                        </div>
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <div className="font-medium text-gray-900">📖 Content</div>
                      <div className="text-gray-600 mb-2">The actual teaching content (supports markdown)</div>
                      <div className="bg-gray-50 p-3 rounded text-xs font-mono">
                        ## Subscription Cancellation<br/>
                        <br/>
                        To cancel your subscription:<br/>
                        1. Go to Account Settings<br/>
                        2. Click "Billing & Subscription"<br/>
                        3. Select "Cancel Subscription"<br/>
                        <br/>
                        **Note**: Cancellation takes effect at the end of your current billing period.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-eddy-green text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Save and Review</h5>
                  <p className="text-gray-700 text-sm mb-2">Click "Create" to save the teaching content. It will be created with status "pending" for your review.</p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                    <div className="font-medium text-yellow-800 mb-1">⏸️ Initial Status: Pending</div>
                    <div className="text-yellow-700">
                      Unlike chat teaching (which you approve immediately), admin-created content starts as "pending" 
                      and appears in the list below for your review and approval.
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-eddy-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">4</div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Approve from List</h5>
                  <p className="text-gray-700 text-sm">Find your new teaching content in the list below, click to view details, then approve or reject as needed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Differences */}
          <div className="border border-eddy-orange/30 rounded-lg overflow-hidden">
            <div className="bg-eddy-orange/10 px-4 py-3 border-b border-eddy-orange/30">
              <h4 className="font-semibold text-eddy-orange">Admin Create vs Chat Teaching</h4>
            </div>
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold text-gray-900">Aspect</th>
                      <th className="text-left py-2 font-semibold text-eddy-blue">💬 Chat Teaching Methods</th>
                      <th className="text-left py-2 font-semibold text-eddy-green">⚙️ Admin Create Teaching</th>
                    </tr>
                  </thead>
                  <tbody className="space-y-2">
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Form Workflow</td>
                      <td className="py-2 text-gray-600">AI preview → Review → Approve/Refine</td>
                      <td className="py-2 text-gray-600">Direct form submission</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Content Generation</td>
                      <td className="py-2 text-gray-600">AI-powered with GPT-4 enhancement</td>
                      <td className="py-2 text-gray-600">Manual input only (no AI)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Context Awareness</td>
                      <td className="py-2 text-gray-600">Uses conversation history</td>
                      <td className="py-2 text-gray-600">No conversation context</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Preview & Refinement</td>
                      <td className="py-2 text-gray-600">Interactive preview with iteration</td>
                      <td className="py-2 text-gray-600">No preview - direct save</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Content Quality Control</td>
                      <td className="py-2 text-gray-600">AI-enhanced & user-approved</td>
                      <td className="py-2 text-gray-600">Depends entirely on your input</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-700">Best Use Case</td>
                      <td className="py-2 text-gray-600">Real-time corrections & improvements</td>
                      <td className="py-2 text-gray-600">Bulk content creation & planned updates</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-gray-700">Initial Status</td>
                      <td className="py-2 text-gray-600">Pending (approved by user in workflow)</td>
                      <td className="py-2 text-gray-600">Pending (needs admin review later)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tips for Admin Creation */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <BookOpen size={16} className="text-eddy-blue" />
              Tips for Effective Admin Teaching Creation
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-eddy-blue pl-4">
                <h5 className="font-medium text-eddy-blue mb-1">📝 Content Writing</h5>
                <p className="text-gray-700 text-sm">
                  Use markdown formatting for better structure. Include headers (##), bullet points, **bold text**, and numbered lists. 
                  The content will be processed and displayed as formatted HTML.
                </p>
              </div>
              <div className="border-l-4 border-eddy-green pl-4">
                <h5 className="font-medium text-eddy-green mb-1">🎯 Be Specific</h5>
                <p className="text-gray-700 text-sm">
                  Write detailed, comprehensive answers. Include step-by-step instructions, important notes, and edge cases. 
                  This content will directly influence user responses.
                </p>
              </div>
              <div className="border-l-4 border-eddy-orange pl-4">
                <h5 className="font-medium text-eddy-orange mb-1">🏷️ Choose Priority Wisely</h5>
                <p className="text-gray-700 text-sm">
                  High priority content gets 800-1000+ points in search ranking. Use "Critical" sparingly for the most important information. 
                  Most content should be "Medium" priority.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4">Advanced Teaching Features</h3>
        
        <div className="grid grid-cols-1 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <MessageSquare size={16} className="text-eddy-blue" />
              Context-Aware Learning
            </h4>
            <p className="text-gray-700 text-sm mb-2">
              The system automatically captures conversation context including recent questions and answers to create more relevant teaching content.
            </p>
            <div className="bg-gray-50 p-3 rounded text-xs font-mono">
              <div className="text-gray-600">Example conversation context used:</div>
              <div className="mt-1">User: "How do I cancel my order?"</div>
              <div className="text-eddy-blue">Eddy: "You can contact support..."</div>
              <div className="mt-1">User: "teach: Actually, there's a 24-hour policy"</div>
              <div className="text-gray-500 mt-2">→ System uses both Q&A for better content generation</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Zap size={16} className="text-eddy-green" />
              High-Priority Integration
            </h4>
            <p className="text-gray-700 text-sm mb-2">
              Teaching content gets priority scoring that makes it highly influential in responses:
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between items-center bg-red-50 px-2 py-1 rounded">
                <span>Critical Teaching Content</span>
                <span className="font-semibold text-red-600">1000+ points</span>
              </div>
              <div className="flex justify-between items-center bg-orange-50 px-2 py-1 rounded">
                <span>High Priority Teaching</span>
                <span className="font-semibold text-orange-600">800 points</span>
              </div>
              <div className="flex justify-between items-center bg-yellow-50 px-2 py-1 rounded">
                <span>Medium Priority Teaching</span>
                <span className="font-semibold text-yellow-600">600 points</span>
              </div>
              <div className="flex justify-between items-center bg-gray-50 px-2 py-1 rounded">
                <span>Regular Documentation</span>
                <span className="font-semibold text-gray-600">15-40 points</span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <BookOpen size={16} className="text-eddy-orange" />
              Smart Content Generation
            </h4>
            <p className="text-gray-700 text-sm mb-2">
              GPT-4 powered content generation creates professional documentation with:
            </p>
            <ul className="space-y-1 text-sm text-gray-700 ml-4">
              <li>• Proper markdown formatting (headers, bullets, emphasis)</li>
              <li>• Comprehensive coverage with examples and details</li>
              <li>• Structured organization with logical flow</li>
              <li>• Integration with existing conversation context</li>
              <li>• Consistent professional tone and style</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Usage Examples */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4">Common Teaching Examples</h3>
        
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">💼 Process Documentation</h4>
            <div className="bg-gray-50 p-3 rounded font-mono text-sm">
              <div className="text-eddy-blue">teach: Our refund process requires manager approval for amounts over $500</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">❓ FAQ Responses</h4>
            <div className="bg-gray-50 p-3 rounded font-mono text-sm">
              <div className="text-eddy-blue">teach: The system maintenance window runs every Sunday from 2-4 AM EST</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">🔧 Feature Corrections</h4>
            <div className="bg-gray-50 p-3 rounded font-mono text-sm">
              <div className="text-gray-600">User: "How do I export reports?"</div>
              <div className="text-gray-500">Eddy: "Use the Export button..."</div>
              <div className="text-eddy-blue mt-1">teach: Actually, reports are exported via the new Actions menu, not the Export button</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">📚 Policy Updates</h4>
            <div className="bg-gray-50 p-3 rounded font-mono text-sm">
              <div className="text-eddy-blue">teach: As of January 2024, all new accounts require two-factor authentication by default</div>
            </div>
          </div>
        </div>
      </section>

      {/* Admin Management Section */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4 flex items-center gap-2">
          <Eye size={20} />
          Admin Management Interface
        </h3>
        
        <p className="text-gray-700 mb-4">
          This admin interface allows you to review, approve, and manage all teaching content created through the chat system.
        </p>

        {/* Status Flow */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-gray-900 mb-3">Content Lifecycle in Admin</h4>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                <Clock size={12} />pending
              </span>
              <span className="text-gray-700">Awaiting your review</span>
            </div>
            <ArrowRight size={16} className="text-gray-400" />
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-eddy-green/20 text-eddy-green rounded-full text-xs">
                <CheckCircle size={12} />approved
              </span>
              <span className="text-gray-700">Available to all users</span>
            </div>
          </div>
        </div>

        {/* Quick Action Guide */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <RefreshCw size={16} className="text-eddy-blue" />
              Refresh Vector Store
            </h4>
            <p className="text-gray-700 text-sm">Updates the search index with all approved teaching content for optimal performance.</p>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Plus size={16} className="text-eddy-green" />
              Create Teaching
            </h4>
            <p className="text-gray-700 text-sm">Manually create teaching content through the admin interface (starts as pending).</p>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section>
        <h3 className="text-xl font-semibold text-eddy-primary mb-4">Best Practices</h3>
        <div className="space-y-3">
          <div className="border-l-4 border-eddy-green pl-4">
            <h4 className="font-medium text-eddy-green mb-1">Teaching Quality</h4>
            <p className="text-gray-700 text-sm">
              Be specific and comprehensive in your teaching input. The AI will expand on your knowledge, so provide key facts, policies, and context.
            </p>
          </div>
          <div className="border-l-4 border-eddy-blue pl-4">
            <h4 className="font-medium text-eddy-blue mb-1">Context Matters</h4>
            <p className="text-gray-700 text-sm">
              Teach immediately after conversations where you notice gaps or corrections. The system uses conversation context to create better content.
            </p>
          </div>
          <div className="border-l-4 border-eddy-orange pl-4">
            <h4 className="font-medium text-eddy-orange mb-1">Review Generated Content</h4>
            <p className="text-gray-700 text-sm">
              Always review AI-generated content before approving. Use "clarify" to refine if needed. Quality teaching content improves everyone's experience.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="border-t border-gray-200 pt-6 mt-8">
        <p className="text-xs text-gray-500">
          The Interactive Teaching System is the core innovation of Eddy, enabling real-time knowledge enhancement through natural conversation with built-in quality controls and immediate RAG integration.
        </p>
      </div>
    </div>
  );
};